
import { useEffect, useMemo } from "react";
import {
  createFileRoute,
  Outlet,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { useNetworkState } from "@uidotdev/usehooks";
// tanstack-query
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// services
import { getAllNotifications, markNotificationAsRead } from "@/services/noti";

// moment js
import moment from "moment";
// types
import { NotificationItemTypes } from "@/types/noti";

// Helpers
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

// UI
import AlertModal from "@/components/AlertModal";
import { Button } from "@/components/ui/button";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { ScrollArea } from "@/components/ui/scroll-area"; // Assuming ScrollArea exists or use div with overflow

import { Badge } from "@/components/ui/badge";
import { AUTH_KEY } from "@/constants/local-storage-key";
// Icons
import { LogOut, Wifi, WifiOff, MessagesSquare, Bell, FileText, User } from "lucide-react";

// Constants
import siteConfig from "@/config/site";

// zustand
import { useDocumentStore } from "@/zustand/documentStore";

import { version } from "../../package.json";

// Mock Data

export const Route = createFileRoute("/_layout-private")({
  component: SideBar,
});

// Helper for navigation
// Helper for navigation

// Helper component for Notification Item
const NotificationItem = ({
  item,
  onClick,
}: {
  item: NotificationItemTypes;
  onClick: () => void;
}) => (
  <div
    onClick={onClick}
    className={`relative p-4 border-b cursor-pointer transition-colors hover:bg-muted/50 ${!item.isRead ? "bg-red-50" : "bg-white"
      }`}
  >
    <div className="flex justify-between items-start mb-2 gap-2">
      <h5 className="font-semibold text-sm leading-tight text-foreground">
        {item.title}
      </h5>
      <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
        {moment(item.createdAt).fromNow()}
      </span>
    </div>

    {/* <div className="flex items-center gap-2 mb-1.5">
      <FileText size={14} className="text-primary shrink-0" />
      <p className="text-xs font-medium text-primary line-clamp-1">
        {item.documentName}
      </p>
    </div> */}

    <p className="text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
      {item.content}
    </p>

    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground bg-muted/30 p-1.5 rounded-md w-fit">
      <User size={12} />
      <span>
        ຜູ້ສ້າງ: <span className="font-medium text-foreground">{item.createdBy}</span>
      </span>
    </div>

    {!item.isRead && (
      <div className="absolute top-4 right-4 w-2 h-2 bg-red-500 rounded-full" />
    )}
  </div>
);

function SideBar() {
  const network = useNetworkState();
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const userString = localStorage.getItem(AUTH_KEY);
  const userJson = userString ? JSON.parse(userString) : null;
  const [activeMenu, setActiveMenu] = useState("all");

  const { fetchDocuments } = useDocumentStore();

  const findBy = {
    limit: 100,
    skip: 0,
  };

  useEffect(() => {
    fetchDocuments(findBy);
  }, [fetchDocuments]);


  const findBys = useMemo(() => {
    if (activeMenu === "all") {
      return {};
      // } else if (activeMenu === "unread") {
      //   return { isRead: false };
      // } else if (activeMenu === "read") {
      //   return { isRead: true };
      // }
    }
    // return {};
  }, [activeMenu]);

  // get notifications from service
  const { data: notifications } = useQuery({
    queryKey: ["notifications", activeMenu, findBys],
    queryFn: () => getAllNotifications(findBys),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const NotiRequestEdit = notifications?.data?.filter(
    (n: NotificationItemTypes) => n.editRequestStatus === "APPROVED" || n.editRequestStatus === "REJECTED"
  );

  const readCount =
    NotiRequestEdit?.filter((n: NotificationItemTypes) => n.isRead === true)
      .length || 0;

  const unreadCount =
    NotiRequestEdit?.filter((n: NotificationItemTypes) => n.isRead === false)
      .length || 0;

  console.log({ readCount, unreadCount });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (notificationId: string) =>
      markNotificationAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    const savedToken = localStorage.getItem(AUTH_KEY); // Replace with your key
    if (!savedToken) {
      navigate({
        to: "/",
        search: {
          active: "riskAssessment",
        },
      });
    }
  }, [navigate]);
  // console.log(aws + userJson?.profileUrl, "profileUrl");
  // Function
  const handleLogout = () => {
    clearLocalStorage();
    navigate({
      to: "/",
      search: {
        active: "riskAssessment",
      },
    });
  };

  const getNetworkQuality = () => {
    if (!network?.online)
      return {
        status: "ຂາດການເຊື່ອມຕໍ່ອິນເຕີເນັດ",
        color: "text-red-500",
        icon: WifiOff,
      };

    // Based on effectiveType
    if (network.effectiveType === "4g") {
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວສູງ",
        color: "text-green-500",
        icon: Wifi,
      };
    }
    if (network.effectiveType === "3g") {
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວປານກາງ",
        color: "text-yellow-500",
        icon: Wifi,
      };
    }
    if (network.effectiveType === "2g" || network.effectiveType === "slow-2g") {
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວຕ່ຳ",
        color: "text-orange-500",
        icon: Wifi,
      };
    }

    // Fallback based on downlink if effectiveType is not available
    if (network.downlink) {
      if (network.downlink > 5) {
        return {
          status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວສູງ",
          color: "text-green-500",
          icon: Wifi,
        };
      }
      if (network.downlink > 2) {
        return {
          status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວປານກາງ",
          color: "text-yellow-500",
          icon: Wifi,
        };
      }
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວຕ່ຳ",
        color: "text-orange-500",
        icon: Wifi,
      };
    }

    // Default fallback
    return {
      status: "ເຊື່ອມຕໍ່ອິນເຕີເນັດແລ້ວ",
      color: "text-green-500",
      icon: Wifi,
    };
  };

  const networkQuality = getNetworkQuality();

  const typeNavigation = {
    FINANCIAL_PLAN_REPORT: "FINANCIAL_PLAN_REPORT",
    TAX_OBLIGATION_PLAN: "TAX_OBLIGATION_PLAN",
    DIVIDEND_PLAN_REPORT: "DIVIDEND_PLAN_REPORT",
    ASSET_REPORT: "ASSET_REPORT",
    DEBT_REPORT: "DEBT_REPORT",
    DIRECT_CASH_FLOW_REPORT: "DIRECT_CASH_FLOW_REPORT",
    INDIRECT_CASH_FLOW_REPORT: "INDIRECT_CASH_FLOW_REPORT",
    OWNER_EQUITY_CHANGE_REPORT: "OWNER_EQUITY_CHANGE_REPORT",
    FINANCIAL_REPORT: "FINANCIAL_REPORT",
    FIX_ASSET_REPORT: "FIX_ASSET_REPORT",
    INDICATOR_REPORT: "INDICATOR_REPORT",
    TAX_OBLIGATION_EXECUTION: "TAX_OBLIGATION_EXECUTION",
    DIVIDEND_EXECUTION_REPORT: "DIVIDEND_EXECUTION_REPORT",
  }


  const handleNavigateToAction = (
    actionType: string,
    relatedId?: string
  ) => {
    if (actionType === typeNavigation.FINANCIAL_PLAN_REPORT) {
      navigate({
        to: "/report/action-plan?page=1&startYear=0&endYear=0",
      } as any);
    } else if (actionType === typeNavigation.TAX_OBLIGATION_PLAN) {
      navigate({
        to: "/report/tax-obligations?active=taxLiabilityPlan&startYear=0&endYear=0",
      } as any);
    } else if (actionType === typeNavigation.DIVIDEND_PLAN_REPORT) {
      navigate({
        to: "/report/tax-obligations?active=dividendPlan&startYear=0&endYear=0",
      } as any);
    } else if (actionType === typeNavigation.ASSET_REPORT) {
      navigate({
        to: "/report/finance/company/finance-property",
      } as any);
    } else if (actionType === typeNavigation.DEBT_REPORT) {
      navigate({
        to: "/report/finance/company/finance-debt",
      } as any);
    } else if (actionType === typeNavigation.DIRECT_CASH_FLOW_REPORT) {
      navigate({
        to: "/report/finance/company/cash-flow-direct-method",
      } as any);
    } else if (actionType === typeNavigation.INDIRECT_CASH_FLOW_REPORT) {
      navigate({
        to: "/report/finance/company/cash-flow-indirect-method",
      } as any);
    } else if (actionType === typeNavigation.FINANCIAL_REPORT) {
      navigate({
        to: "/report/finance/company/result",
      } as any);
    } else if (actionType === typeNavigation.OWNER_EQUITY_CHANGE_REPORT) {
      navigate({
        to: "/report/finance/company/owner-equity",
      } as any);
    } else if (actionType === typeNavigation.FIX_ASSET_REPORT) {
      navigate({
        to: "/report/finance/company/fix-assets",
      } as any);
    } else if (actionType === typeNavigation.INDICATOR_REPORT) {
      navigate({
        to: "/report/finance/company/indicators",
      } as any);
    } else if (relatedId) {
      navigate({
        to: "/report/overview",
        search: {
          docId: relatedId.toString(),
        },
      } as any);
    }
  };

  const handleViewDetails = (
    notificationId: string,
    isRead: boolean,
    actionType: string,
    relatedId?: string
  ) => {
    if (!isRead) {
      mutation.mutate(notificationId);
    }
    handleNavigateToAction(actionType, relatedId);
    setIsPopoverOpen(false);
  };

  return (
    <div className="flex flex-row h-screen">
      <aside className="w-[20%] h-full border-r bg-primary flex flex-col">
        <div
          className="flex flex-col items-center flex-shrink-0 w-full gap-4 p-4"
          style={{
            boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.1)",
          }}
        >
          <img src="/soei_logo.png" alt="logo" width={64} height={64} />

          <div className="flex flex-col items-center justify-center gap-1">
            <h2 className="text-sm font-bold text-white">
              ລະບົບ ຄຸ້ມຄອງວິສາຫະກິດລັດລົງທຶນ
            </h2>

            <small className="font-semibold text-white">
              ກົມຄຸ້ມຄອງວິສາຫະກິດລັດລົງທຶນ ແລະ ການປະກັນໄພ
            </small>
            {/* <h3 className="text-sm text-white">ກະຊວງການເງິນ</h3> */}
          </div>
        </div>
        <nav className="mt-[40px] px-4 flex-1 overflow-y-auto">
          {siteConfig.sideBarMenus.map((menu) => (
            <div key={menu.title}>
              <h4 className="px-4 py-2 text-[10px] text-gray-300 font-semibold">
                {menu.title}
              </h4>
              <ul>
                {menu.navItems.map((navItem) => (
                  <li key={navItem.name}>
                    <Link
                      to={navItem.link}
                      className="px-4 py-2 rounded-md text-white flex items-center [&.active]:font-bold [&.active]:bg-muted [&.active]:text-primary  text-[14px] "
                    >
                      <navItem.icon className="mr-2" size={20} />
                      {navItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="flex flex-col space-y-1 my-3 text-center text-sm text-white">
            <p>Version {version}</p>
          </div>
        </nav>
      </aside>
      <div className="relative w-[80%] h-screen overflow-y-auto  ">
        <header className="sticky top-0 z-10 flex items-center justify-between w-full px-8 py-4 bg-white border-b">
          <div>
            <Badge
              variant={"secondary"}
              className="flex items-center gap-2 px-3 py-1"
            >
              <div
                className={`flex gap - 2 items - center ${networkQuality.color} `}
              >
                <networkQuality.icon className="w-4 h-4" />
                <span>{networkQuality.status}</span>
                {network?.downlink && (
                  <span className="ml-1 text-xs">
                    ({network.downlink.toFixed(1)} Mbps)
                  </span>
                )}
              </div>
            </Badge>
          </div>
          <div className="space-x-[24px] flex items-center">
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative w-14 h-14">
                  <Bell size={20} />
                  {NotiRequestEdit?.some((n: NotificationItemTypes) => !n.isRead) && (
                    <span className="absolute bg-red-500 text-white w-6 h-6 flex items-center justify-center top-1 right-1 text-xs font-semibold text-red-500 rounded-full border-2 border-white" >
                      {NotiRequestEdit?.filter((n: NotificationItemTypes) => !n.isRead).length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="end">
                <div className="p-4 border-b">
                  <h4 className="font-semibold leading-none">ແຈ້ງເຕືອນ</h4>
                </div>
                <Tabs defaultValue="all" className="w-full">
                  <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
                    <TabsTrigger
                      value="all"
                      className="relative rounded-none border-b-2 border-transparent px-4 py-2 font-semibold text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                    >
                      ທັງໝົດ
                    </TabsTrigger>
                    <TabsTrigger
                      value="unread"
                      className="relative rounded-none border-b-2 border-transparent px-4 py-2 font-semibold text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                    >
                      ຍັງບໍ່ທັນອ່ານ
                    </TabsTrigger>
                    <TabsTrigger
                      value="read"
                      className="relative rounded-none border-b-2 border-transparent px-4 py-2 font-semibold text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                    >
                      ອ່ານແລ້ວ
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="all" className="m-0">
                    <div className="max-h-[300px] overflow-y-auto">
                      {NotiRequestEdit?.length > 0 ? (
                        NotiRequestEdit?.map((item: NotificationItemTypes) => (
                          <NotificationItem
                            key={item.id}
                            item={item}
                            onClick={() =>
                              handleViewDetails(
                                item.id,
                                item.isRead,
                                item.actionType,
                                item.relatedId
                              )
                            }
                          />
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">ບໍ່ມີລາຍການແຈ້ງເຕືອນ</div>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="unread" className="m-0">
                    <div className="max-h-[300px] overflow-y-auto">
                      {NotiRequestEdit?.filter((n: NotificationItemTypes) => !n.isRead).length > 0 ? (
                        NotiRequestEdit?.filter((n: NotificationItemTypes) => !n.isRead).map((item: NotificationItemTypes) => (
                          <NotificationItem
                            key={item.id}
                            item={item}
                            onClick={() =>
                              handleViewDetails(
                                item.id,
                                item.isRead,
                                item.actionType,
                                item.relatedId
                              )
                            }
                          />
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">ບໍ່ມີລາຍການແຈ້ງເຕືອນໃໝ່</div>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="read" className="m-0">
                    <div className="max-h-[300px] overflow-y-auto">
                      {NotiRequestEdit?.filter((n: NotificationItemTypes) => n.isRead).length > 0 ? (
                        NotiRequestEdit?.filter((n: NotificationItemTypes) => n.isRead).map((item: NotificationItemTypes) => (
                          <NotificationItem
                            key={item.id}
                            item={item}
                            onClick={() =>
                              handleViewDetails(
                                item.id,
                                item.isRead,
                                item.actionType,
                                item.relatedId
                              )
                            }
                          />
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">ບໍ່ມີລາຍການທີ່ອ່ານແລ້ວ</div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </PopoverContent>
            </Popover>

            <Button
              onClick={() =>
                navigate({
                  to: "/chat",
                })
              }
            >
              <MessagesSquare size={20} className="mr-2" />
              ການສົນທະນາ
            </Button>

            <AlertModal
              title="ທ່ານແນ່ໃຈບໍ ທີ່ຈະອອກຈາກລະບົບ?"
              triggerComponent={
                <Button variant={"outline"}>
                  <LogOut size={20} className="mr-2" />
                  ອອກຈາລະບົບ
                </Button>
              }
              confirmFunction={handleLogout}
            />
          </div>
        </header>
        <main className="py-[24px] px-[32px] ">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
